import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { v4 } from 'uuid';
import { DB, Storage } from '~/services/firebase';
import { useAuth } from '~/hooks/auth';

import Dashboard from '~/layouts/Dashboard';
import getValidationErrors from '~/utils/getValidationErrors';
import { useToast } from '~/hooks/toast';
import IComplement from '~/interfaces/IComplement';
import IComplementItem from '~/interfaces/IComplementItem';
import { bff } from '~/services/bff';
import { useProductsCompany } from '~/hooks/productsCompany';
// import { useProductsCompany } from '~/hooks/productsCompany';

interface Params {
  id: string;
  item: string;
}

interface StateProps {
  item: IComplement;
}

const { REACT_APP_FIREBASE_PROJECT } = process.env;

const ComplementItemForm: React.FC = () => {
  const history = useHistory();
  const params = useParams<Params>();
  const location = useLocation<StateProps>();
  const { user, company } = useAuth();
  const { addToast } = useToast();
  const { updateComplements } = useProductsCompany();
  const [complement, setComplement] = useState({} as IComplement);
  const [data, setData] = useState({} as IComplementItem);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const [index, setIndex] = useState(-1);
  const [existingGroups, setExistingGroups] = useState<string[]>([]);
  const { id, item: itemId } = params;

  useEffect(() => {
    async function loadItem(comp?: IComplement) {
      let item = {} as IComplement;

      if (comp) {
        setComplement(comp);
        item = comp;
      } else {
        const productsRef = doc(DB, 'complements', id);
        const docSnap = await getDoc(productsRef);
        item = docSnap.data() as IComplement;

        setComplement({
          ...item,
          uid: id,
        } as IComplement);
      }

      // Extrair grupos existentes
      if (item.items && item.items.length > 0) {
        const groups = item.items
          .filter(i => i.groupTitle && i.groupTitle.trim() !== '')
          .map(i => i.groupTitle!.trim())
          .filter((group, index, arr) => arr.indexOf(group) === index); // Remover duplicados
        setExistingGroups(groups.sort());
      }

      if (itemId) {
        const findItem = item.items.findIndex(find => find.uid === itemId);

        if (findItem !== -1) {
          setIndex(findItem);
          setData(item.items[findItem]);
        } else {
          history.goBack();
        }
      } else {
        setData({
          status: true,
          description: '',
          image: '',
        } as IComplementItem);
      }
    }

    if (location.state && location.state.item) {
      loadItem(location.state.item);
    } else if (id) loadItem();
    else history.goBack();
  }, [id, history, itemId, location]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        const schema = Yup.object().shape({
          name: Yup.string().required('O nome é obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const { name, description, value, status, groupTitle } = data;

        const inputFile = document.getElementById('photo') as HTMLInputElement;

        let image = '';

        if (inputFile && inputFile.files && inputFile.files.length > 0) {
          const file = inputFile.files[0];
          const storageRef = Storage.ref(`complements/${v4()}.png`);
          const resposne = await storageRef.put(file);
          image = await resposne.ref.getDownloadURL();
        }

        const uid = data.uid || v4();

        let parseData: any = {
          uid,
          name,
          description,
          value: value || 0,
          user_id: user.uid,
          company_id: company.uid,
          status,
        };

        if (groupTitle) parseData = { groupTitle, ...parseData };

        if (image) parseData = { image, ...parseData };

        const updateItems =
          complement && complement.items ? [...complement.items] : [];

        if (index !== -1) {
          updateItems[index] = parseData;
        } else {
          updateItems.push(parseData);
        }

        updateItems.sort((a, b) =>
          a.name > b.name ? 1 : b.name > a.name ? -1 : 0,
        );

        await updateDoc(doc(DB, 'complements', id), {
          items: updateItems,
        });

        updateComplements({
          ...complement,
          items: updateItems,
        });

        await bff.delete(`/complements/${REACT_APP_FIREBASE_PROJECT}`);

        addToast({
          type: 'success',
          description: data.uid
            ? 'Item alterado com sucesso!'
            : 'Item criado com sucesso!',
        });

        history.push(`/complements/items/${id}`);

        setLoading(false);
      } catch (err: any) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [
      user,
      data,
      addToast,
      history,
      company,
      complement,
      index,
      id,
      updateComplements,
    ],
  );

  return (
    <Dashboard containerMessage="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
      <div className="row">
        <div className="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <div className="nk-block-head-sub">
                  <Link className="back-to" to={`/complements/items/${id}`}>
                    <em className="icon ni ni-arrow-left" />
                    <span>Voltar</span>
                  </Link>
                </div>
                <h4 className="title nk-block-title">
                  {itemId ? 'Editar Item' : 'Criar Item'}
                </h4>
                <div className="nk-block-des">
                  <p>Preencha o formulário abaixo e crie um novo item.</p>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <div className="card-inner">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Nome</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.name}
                            onChange={event => {
                              setData({ ...data, name: event.target.value });
                            }}
                          />
                          {errors.name && (
                            <span className="invalid">{errors.name}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Valor</label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            step=".01"
                            className="form-control"
                            defaultValue={data.value}
                            onChange={event => {
                              setData({ ...data, value: event.target.value });
                            }}
                          />
                          {errors.value && (
                            <span className="invalid">{errors.value}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* <div className="col-lg-12">
                      <div className="form-group mb-3">
                        <label className="form-label">Imagem</label>
                        <div className="form-control-wrap">
                          <input
                            type="file"
                            id="photo"
                            style={{ width: '100%' }}
                          />
                        </div>
                      </div>
                    </div> */}

                    <div className="col-lg-12">
                      <div className="form-group mb-3">
                        <label
                          className="form-label"
                          htmlFor="default-textarea"
                        >
                          Descrição
                        </label>
                        <div className="form-control-wrap">
                          <textarea
                            className="form-control no-resize"
                            id="default-textarea"
                            placeholder="Informe uma descrição para o produto..."
                            defaultValue={data.description}
                            onChange={event => {
                              setData({
                                ...data,
                                description: event.target.value,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-12">
                      <div className="form-group mb-3">
                        <label className="form-label">Agrupar por Título</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Ex: Sabores Tradicionais"
                            list="existing-groups"
                            defaultValue={data.groupTitle || ''}
                            onChange={event => {
                              setData({ ...data, groupTitle: event.target.value });
                            }}
                          />
                          <datalist id="existing-groups">
                            {existingGroups.map((group, index) => (
                              <option key={index} value={group} />
                            ))}
                          </datalist>
                          {existingGroups.length > 0 && (
                            <small className="text-muted mt-1 d-block">
                              💡 Clique para ver grupos existentes ou digite um novo
                            </small>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Status do produto?</label>
                        <ul className="custom-control-group g-3 align-center">
                          <li>
                            <div className="custom-control custom-control-sm custom-checkbox">
                              <input
                                id="active"
                                type="checkbox"
                                className="custom-control-input"
                                defaultChecked={data.status}
                                onClick={() => {
                                  setData({
                                    ...data,
                                    status: !data.status,
                                  });
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="active"
                              >
                                Ativo
                              </label>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <button
                          type="submit"
                          className="btn btn-lg btn-primary"
                          disabled={loading}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Salvar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default ComplementItemForm;
